import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import classnames from 'classnames';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Label,
  Progress,
  Badge,
  Collapse,
} from 'reactstrap';

import { Can } from '../../utilities/Can';
import { get, post } from '../../helpers/api_helper';
import TagSelector from '../Common/TagSelector';

const EXPORT_START_ENDPOINT = '/api/bc/products/export';

const OPERATOR_OPTIONS = {
  text: [
    { value: 'eq', label: 'Equals' },
    { value: 'like', label: 'Contains / like' },
    { value: 'in', label: 'In list' },
    { value: 'not_in', label: 'Not in list' },
  ],
  number: [
    { value: 'eq', label: 'Equals' },
    { value: 'greater', label: 'Greater than' },
    { value: 'min', label: 'Greater than or equal' },
    { value: 'less', label: 'Less than' },
    { value: 'max', label: 'Less than or equal' },
    { value: 'in', label: 'In list' },
    { value: 'not_in', label: 'Not in list' },
  ],
  date: [
    { value: 'eq', label: 'Equals' },
    { value: 'greater', label: 'After' },
    { value: 'min', label: 'On or after' },
    { value: 'less', label: 'Before' },
    { value: 'max', label: 'On or before' },
  ],
  boolean: [
    { value: 'eq', label: 'Is' },
  ],
  tags: [
    { value: 'in', label: 'Includes tags' },
  ],
};

const PRODUCT_FILTER_FIELDS = [
  {
    key: 'id',
    label: 'Product ID',
    type: 'number',
    placeholder: '12345 or 12345,67890',
  },
  {
    key: 'sku',
    label: 'SKU',
    type: 'text',
    placeholder: 'SJ-12345',
  },
  {
    key: 'name',
    label: 'Name',
    type: 'text',
    placeholder: 'Samsung main board',
  },
  {
    key: 'search_keywords',
    label: 'Search Keywords',
    type: 'text',
    placeholder: 'jar, glass',
  },
  {
    key: 'brand_id',
    label: 'Brand ID',
    type: 'number',
    placeholder: '77',
  },
  {
    key: 'categories',
    label: 'Category IDs',
    type: 'number',
    placeholder: '12,18,44',
  },
  {
    key: 'price',
    label: 'Price',
    type: 'number',
    placeholder: '49.99',
  },
  {
    key: 'inventory_level',
    label: 'Inventory Level',
    type: 'number',
    placeholder: '5',
  },
  {
    key: 'type',
    label: 'Type',
    type: 'text',
    placeholder: 'physical or digital',
  },
  {
    key: 'availability',
    label: 'Availability',
    type: 'text',
    placeholder: 'available, disabled, preorder',
  },
  {
    key: 'is_visible',
    label: 'Visible',
    type: 'boolean',
    placeholder: '',
  },
  {
    key: 'is_featured',
    label: 'Featured',
    type: 'boolean',
    placeholder: '',
  },
  {
    key: 'upc',
    label: 'UPC',
    type: 'text',
    placeholder: '012345678901',
  },
  {
    key: 'mpn',
    label: 'MPN',
    type: 'text',
    placeholder: 'BN94-12345A',
  },
  {
    key: 'gtin',
    label: 'GTIN',
    type: 'text',
    placeholder: '00012345600012',
  },
  {
    key: 'date_modified',
    label: 'Date Modified',
    type: 'date',
    placeholder: '2026-04-27',
  },
];

const LISTING_FILTER_FIELDS = [
  {
    key: 'last_qty',
    label: 'Last Qty',
    type: 'number',
    placeholder: '1',
  },
  {
    key: 'title',
    label: 'Title',
    type: 'text',
    placeholder: 'Samsung main board',
  },
  {
    key: 'sku',
    label: 'SKU',
    type: 'text',
    placeholder: 'SJ-12345',
  },
  {
    key: 'tags',
    label: 'Tags',
    type: 'tags',
    placeholder: '',
  },
];

const TERMINAL_EXPORT_STATUSES = ['completed', 'failed', 'error', 'cancelled'];
const ACTIVE_EXPORT_STATUSES = ['queued', 'pending', 'running', 'processing'];

const createInitialFilterState = (fields) => fields.reduce((acc, field) => {
  const defaultOperator = OPERATOR_OPTIONS[field.type][0].value;
  let defaultValue = '';

  if (field.type === 'boolean') {
    defaultValue = 'true';
  } else if (field.type === 'tags') {
    defaultValue = [];
  }

  acc[field.key] = {
    enabled: false,
    operator: defaultOperator,
    value: defaultValue,
  };

  return acc;
}, {});

const normalizeFilterValue = (field, operator, rawValue) => {
  if (field.type === 'tags') {
    return rawValue.map((tag) => tag.id).filter((id) => id != null);
  }

  if (field.type === 'boolean') {
    return rawValue === 'true';
  }

  if (operator === 'in' || operator === 'not_in') {
    return rawValue
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value !== '')
      .map((value) => (field.type === 'number' ? Number(value) : value));
  }

  if (field.type === 'number') {
    return Number(rawValue);
  }

  return rawValue.trim();
};

const hasFieldValue = (field, filterState) => {
  if (!filterState.enabled) return false;
  if (field.type === 'boolean') return true;
  if (field.type === 'tags') return filterState.value.length > 0;

  return filterState.value.toString().trim() !== '';
};

const buildFilterObjects = (fields, filters) => fields
  .filter((field) => hasFieldValue(field, filters[field.key]))
  .map((field) => ({
    field: field.key,
    operator: filters[field.key].operator,
    value: normalizeFilterValue(
      field,
      filters[field.key].operator,
      filters[field.key].value,
    ),
  }));

const coercePercent = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.min(100, Math.max(0, value));
  }

  return null;
};

const computePercentFromPages = (pagesCompleted, pagesTotal) => {
  if (!Number.isFinite(pagesCompleted) || !Number.isFinite(pagesTotal)) {
    return null;
  }

  if (pagesTotal <= 0) {
    return null;
  }

  return coercePercent((pagesCompleted / pagesTotal) * 100);
};

const buildStatusUrl = (jobId) => `${EXPORT_START_ENDPOINT}/${jobId}/status`;
const buildDownloadUrl = (jobId) => `${EXPORT_START_ENDPOINT}/${jobId}/download`;

const getAuthToken = () => {
  try {
    const authUser = JSON.parse(localStorage.getItem('authUser'));

    return authUser?.token || null;
  } catch (error) {
    return null;
  }
};

const buildAuthenticatedUrl = (url) => {
  const token = getAuthToken();

  if (!token) return url;

  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}token=${encodeURIComponent(token)}`;
};

const normalizeExportJob = (rawJob) => {
  if (!rawJob) return null;

  const jobId = rawJob.job_id || rawJob.id || null;
  const status = String(rawJob.status || 'queued').toLowerCase();
  const pagesCompleted = rawJob.pages_completed ?? rawJob.pagesCompleted ?? null;
  const pagesTotalEstimate = rawJob.pages_total_estimate ?? rawJob.pagesTotalEstimate ?? null;
  const explicitPercent = rawJob.percent ?? rawJob.progress_percent ?? null;
  const percent = coercePercent(explicitPercent)
    ?? computePercentFromPages(pagesCompleted, pagesTotalEstimate);

  return {
    jobId,
    status,
    phase: rawJob.phase || null,
    message: rawJob.message || null,
    recordsProcessed: rawJob.records_processed ?? rawJob.recordsProcessed ?? null,
    pagesCompleted,
    pagesTotalEstimate,
    percent,
    downloadUrl: rawJob.download_url || (jobId ? buildDownloadUrl(jobId) : null),
    statusUrl: rawJob.status_url || (jobId ? buildStatusUrl(jobId) : null),
    error: rawJob.error || rawJob.error_message || null,
    raw: rawJob,
  };
};

const getStatusBadgeColor = (status) => {
  if (status === 'completed') return 'success';
  if (status === 'failed' || status === 'error' || status === 'cancelled') return 'danger';
  if (status === 'running' || status === 'processing') return 'primary';

  return 'warning';
};

function ProductQueryCard(props) {
  const { t } = props;

  const [activeQueryTab, setActiveQueryTab] = useState('bigcommerce');
  const [productFilters, setProductFilters] = useState(
    () => createInitialFilterState(PRODUCT_FILTER_FIELDS),
  );
  const [listingFilters, setListingFilters] = useState(
    () => createInitialFilterState(LISTING_FILTER_FIELDS),
  );
  const [productQueryLoading, setProductQueryLoading] = useState(false);
  const [productQueryError, setProductQueryError] = useState(null);
  const [productQueryLimit, setProductQueryLimit] = useState('50');
  const [exportJob, setExportJob] = useState(null);
  const [showExportPreview, setShowExportPreview] = useState(false);

  const exportRequest = useMemo(() => ({
    limit: Number(productQueryLimit),
    output: 'csv',
    match_mode: 'intersection',
    bigcommerce_filters: buildFilterObjects(PRODUCT_FILTER_FIELDS, productFilters),
    listing_filters: buildFilterObjects(LISTING_FILTER_FIELDS, listingFilters),
  }), [listingFilters, productFilters, productQueryLimit]);

  const updateFilterState = (setter, fieldKey, updates) => {
    setter((current) => ({
      ...current,
      [fieldKey]: {
        ...current[fieldKey],
        ...updates,
      },
    }));
  };

  const resetProductQuery = () => {
    setProductFilters(createInitialFilterState(PRODUCT_FILTER_FIELDS));
    setListingFilters(createInitialFilterState(LISTING_FILTER_FIELDS));
    setProductQueryError(null);
    setProductQueryLimit('50');
    setActiveQueryTab('bigcommerce');
    setExportJob(null);
  };

  const refreshExportJob = async (statusUrlOverride = null) => {
    if (exportJob === null && statusUrlOverride === null) return;

    const statusUrl = statusUrlOverride || exportJob.statusUrl;

    if (!statusUrl) return;

    try {
      const response = await get(statusUrl);
      setExportJob((current) => {
        const baseJob = current || exportJob;
        const normalized = normalizeExportJob({
          ...baseJob?.raw,
          ...response,
        });

        return {
          ...baseJob,
          ...normalized,
        };
      });
    } catch (error) {
      setExportJob((current) => ({
        ...current,
        status: 'error',
        error: error.message,
        message: error.message,
      }));
    }
  };

  useEffect(() => {
    if (exportJob === null) return undefined;
    if (!ACTIVE_EXPORT_STATUSES.includes(exportJob.status)) return undefined;
    if (!exportJob.statusUrl) return undefined;

    const timeoutId = setTimeout(() => {
      refreshExportJob(exportJob.statusUrl);
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [exportJob]);

  const startExport = async () => {
    const hasValidFilters = exportRequest.bigcommerce_filters.length > 0
      || exportRequest.listing_filters.length > 0;

    if (!hasValidFilters) {
      setProductQueryError(t('Select at least one filter and enter a value.'));
      setExportJob(null);
      return;
    }

    try {
      setProductQueryLoading(true);
      setProductQueryError(null);

      const response = await post(EXPORT_START_ENDPOINT, exportRequest);
      const normalizedJob = normalizeExportJob(response) || {};

      setExportJob({
        ...normalizedJob,
        jobId: normalizedJob.jobId,
        status: normalizedJob.status || 'queued',
        statusUrl: normalizedJob.statusUrl
          || (normalizedJob.jobId ? buildStatusUrl(normalizedJob.jobId) : null),
        downloadUrl: normalizedJob.downloadUrl
          || (normalizedJob.jobId ? buildDownloadUrl(normalizedJob.jobId) : null),
      });
    } catch (error) {
      setExportJob(null);
      setProductQueryError(error.message);
    } finally {
      setProductQueryLoading(false);
    }
  };

  const progressValue = exportJob?.percent ?? 100;
  const isProgressKnown = exportJob?.percent != null;
  const isExportActive = exportJob !== null
    && ACTIVE_EXPORT_STATUSES.includes(exportJob.status);
  const isExportComplete = exportJob?.status === 'completed';
  const isExportTerminal = exportJob !== null
    && TERMINAL_EXPORT_STATUSES.includes(exportJob.status);

  return (
    <Card>
      <CardHeader className="bg-transparent border-bottom">
        {t('Product Export')}
      </CardHeader>
      <CardBody>
        <Can i="see" this="bc">
          <p className="text-muted mb-3">
            {t(
              'Starts a CSV export job that matches both BigCommerce and local Listing filters.',
            )}
          </p>

          {productQueryError !== null && (
            <Alert color="danger">{productQueryError}</Alert>
          )}

          <Nav tabs className="nav-tabs-custom mb-3">
            <NavItem>
              <NavLink
                className={classnames({
                  active: activeQueryTab === 'bigcommerce',
                })}
                onClick={() => { setActiveQueryTab('bigcommerce'); }}
                style={{ cursor: 'pointer' }}
              >
                {t('BigCommerce')}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({
                  active: activeQueryTab === 'listings',
                })}
                onClick={() => { setActiveQueryTab('listings'); }}
                style={{ cursor: 'pointer' }}
              >
                {t('Listings')}
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeQueryTab}>
            <TabPane tabId="bigcommerce">
              <div className="d-flex flex-column gap-3">
                {PRODUCT_FILTER_FIELDS.map((field) => (
                  <Row key={field.key} className="g-2 align-items-center">
                    <Col md="3">
                      <div className="form-check">
                        <input
                          id={`product-filter-${field.key}`}
                          type="checkbox"
                          className="form-check-input"
                          checked={productFilters[field.key].enabled}
                          onChange={(e) => {
                            updateFilterState(setProductFilters, field.key, {
                              enabled: e.target.checked,
                            });
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`product-filter-${field.key}`}
                        >
                          {t(field.label)}
                        </label>
                      </div>
                    </Col>

                    <Col md="3">
                      <select
                        className="form-control"
                        disabled={!productFilters[field.key].enabled || isExportActive}
                        value={productFilters[field.key].operator}
                        onChange={(e) => {
                          updateFilterState(setProductFilters, field.key, {
                            enabled: true,
                            operator: e.target.value,
                          });
                        }}
                      >
                        {OPERATOR_OPTIONS[field.type].map((option) => (
                          <option
                            key={`${field.key}-${option.value}`}
                            value={option.value}
                          >
                            {t(option.label)}
                          </option>
                        ))}
                      </select>
                    </Col>

                    <Col md="6">
                      {field.type === 'boolean' && (
                        <select
                          className="form-control"
                          disabled={!productFilters[field.key].enabled || isExportActive}
                          value={productFilters[field.key].value}
                          onChange={(e) => {
                            updateFilterState(setProductFilters, field.key, {
                              enabled: true,
                              value: e.target.value,
                            });
                          }}
                        >
                          <option value="true">{t('True')}</option>
                          <option value="false">{t('False')}</option>
                        </select>
                      )}

                      {field.type !== 'boolean' && (
                        <input
                          type={field.type === 'date' ? 'date' : 'text'}
                          className="form-control"
                          disabled={!productFilters[field.key].enabled || isExportActive}
                          placeholder={t(field.placeholder)}
                          value={productFilters[field.key].value}
                          onChange={(e) => {
                            updateFilterState(setProductFilters, field.key, {
                              enabled: true,
                              value: e.target.value,
                            });
                          }}
                        />
                      )}
                    </Col>
                  </Row>
                ))}
              </div>
            </TabPane>

            <TabPane tabId="listings">
              <div className="d-flex flex-column gap-3">
                {LISTING_FILTER_FIELDS.map((field) => (
                  <Row key={field.key} className="g-2 align-items-start">
                    <Col md="3">
                      <div className="form-check mt-2">
                        <input
                          id={`listing-filter-${field.key}`}
                          type="checkbox"
                          className="form-check-input"
                          checked={listingFilters[field.key].enabled}
                          onChange={(e) => {
                            updateFilterState(setListingFilters, field.key, {
                              enabled: e.target.checked,
                            });
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`listing-filter-${field.key}`}
                        >
                          {t(field.label)}
                        </label>
                      </div>
                    </Col>

                    <Col md="3">
                      {field.type !== 'tags' && (
                        <select
                          className="form-control"
                          disabled={!listingFilters[field.key].enabled || isExportActive}
                          value={listingFilters[field.key].operator}
                          onChange={(e) => {
                            updateFilterState(setListingFilters, field.key, {
                              enabled: true,
                              operator: e.target.value,
                            });
                          }}
                        >
                          {OPERATOR_OPTIONS[field.type].map((option) => (
                            <option
                              key={`${field.key}-${option.value}`}
                              value={option.value}
                            >
                              {t(option.label)}
                            </option>
                          ))}
                        </select>
                      )}

                      {field.type === 'tags' && (
                        <div className="pt-2">
                          <Label className="form-label mb-0">
                            {t('Tag Match')}
                          </Label>
                        </div>
                      )}
                    </Col>

                    <Col md="6">
                      {field.type !== 'tags' && (
                        <input
                          type="text"
                          className="form-control"
                          disabled={!listingFilters[field.key].enabled || isExportActive}
                          placeholder={t(field.placeholder)}
                          value={listingFilters[field.key].value}
                          onChange={(e) => {
                            updateFilterState(setListingFilters, field.key, {
                              enabled: true,
                              value: e.target.value,
                            });
                          }}
                        />
                      )}

                      {field.type === 'tags' && (
                        <div
                          style={{
                            opacity: listingFilters[field.key].enabled ? 1 : 0.6,
                            pointerEvents: listingFilters[field.key].enabled
                              && !isExportActive
                              ? 'auto'
                              : 'none',
                          }}
                        >
                          <TagSelector
                            id="listing_query_tag_selector"
                            initialTags={listingFilters[field.key].value}
                            onUpdate={(tags) => {
                              updateFilterState(setListingFilters, field.key, {
                                enabled: true,
                                value: tags,
                                operator: 'in',
                              });
                            }}
                          />
                        </div>
                      )}
                    </Col>
                  </Row>
                ))}
              </div>
            </TabPane>
          </TabContent>

          <Row className="g-2 mt-3">
            <Col md="3">
              <label
                htmlFor="bc-product-query-limit"
                className="form-label mb-1"
              >
                {t('Limit')}
              </label>
              <input
                id="bc-product-query-limit"
                type="number"
                min="1"
                max="250"
                className="form-control"
                disabled={isExportActive}
                value={productQueryLimit}
                onChange={(e) => {
                  setProductQueryLimit(e.target.value);
                }}
              />
            </Col>
          </Row>

          <div className="hstack gap-2 mt-4">
            <Button
              color="primary"
              onClick={startExport}
              disabled={productQueryLoading || isExportActive}
            >
              {productQueryLoading ? t('Starting Export...') : t('Start Export')}
            </Button>
            <Button
              color="light"
              onClick={resetProductQuery}
              disabled={productQueryLoading || isExportActive}
            >
              {t('Reset')}
            </Button>
            {exportJob?.statusUrl && (
              <Button
                color="light"
                onClick={() => { refreshExportJob(exportJob.statusUrl); }}
                disabled={productQueryLoading || isExportActive}
              >
                {t('Refresh Status')}
              </Button>
            )}
            {isExportComplete && exportJob?.downloadUrl && (
              <Button
                tag="a"
                href={buildAuthenticatedUrl(exportJob.downloadUrl)}
                color="success"
              >
                {t('Download CSV')}
              </Button>
            )}
          </div>

          <div className="mt-4">
            <Button
              color="light"
              className="w-100 d-flex justify-content-between align-items-center"
              onClick={() => { setShowExportPreview((current) => !current); }}
            >
              <span>{t('Export Request Preview')}</span>
              <span className={`mdi ${showExportPreview ? 'mdi-chevron-up' : 'mdi-chevron-down'}`} />
            </Button>
            <Collapse isOpen={showExportPreview}>
              <pre
                className="bg-light border rounded p-3 mb-0 mt-2"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {JSON.stringify(exportRequest, null, 2)}
              </pre>
            </Collapse>
          </div>

          {exportJob !== null && (
            <div className="mt-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h5 className="mb-0">{t('Export Progress')}</h5>
                <Badge color={getStatusBadgeColor(exportJob.status)}>
                  {t(exportJob.status)}
                </Badge>
              </div>

              {exportJob.error && (
                <Alert color="danger">{exportJob.error}</Alert>
              )}

              <Progress
                value={progressValue}
                animated={!isProgressKnown || isExportActive}
                striped
                color={isExportTerminal && exportJob.status !== 'completed' ? 'danger' : 'primary'}
              >
                {isProgressKnown ? `${Math.round(progressValue)}%` : ''}
              </Progress>

              <div className="mt-3 d-flex flex-column gap-1 small text-muted">
                <span>
                  <strong>
                    {t('Job ID')}
                    :
                  </strong>
                  {' '}
                  {exportJob.jobId || t('Pending')}
                </span>
                {exportJob.phase && (
                  <span>
                    <strong>
                      {t('Phase')}
                      :
                    </strong>
                    {' '}
                    {exportJob.phase}
                  </span>
                )}
                {exportJob.message && (
                  <span>
                    <strong>
                      {t('Message')}
                      :
                    </strong>
                    {' '}
                    {exportJob.message}
                  </span>
                )}
                {(exportJob.pagesCompleted != null || exportJob.pagesTotalEstimate != null) && (
                  <span>
                    <strong>
                      {t('Pages')}
                      :
                    </strong>
                    {' '}
                    {exportJob.pagesCompleted ?? 0}
                    {' / '}
                    {exportJob.pagesTotalEstimate ?? t('Unknown')}
                  </span>
                )}
                {exportJob.recordsProcessed != null && (
                  <span>
                    <strong>
                      {t('Records Processed')}
                      :
                    </strong>
                    {' '}
                    {exportJob.recordsProcessed}
                  </span>
                )}
                {exportJob.downloadUrl && (
                  <span>
                    <strong>
                      {t('Download URL')}
                      :
                    </strong>
                    {' '}
                    {buildAuthenticatedUrl(exportJob.downloadUrl)}
                  </span>
                )}
              </div>

              <div className="mt-3">
                <h6>{t('Latest Status Payload')}</h6>
                <pre
                  className="bg-light border rounded p-3 mb-0"
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {JSON.stringify(exportJob.raw, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </Can>

        <Can not i="see" this="bc"><p>{t('Unauthorized')}</p></Can>
      </CardBody>
    </Card>
  );
}

ProductQueryCard.defaultProps = {
  t: (x) => x,
};

ProductQueryCard.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(ProductQueryCard);
